import { useMemo, useState } from "react"

import { graphql, useStaticQuery } from "gatsby"
import { IGatsbyImageData } from "gatsby-plugin-image"
import { Trans, useI18next } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import Kicker from "components/TextElements/Kicker"
import ZoomImageGatsby from "components/UI-Related/ZoomImageGatsby"
import bluePenguinBlueSVG from "images/b2b/BluePenguins.webp"
import bluePenguinGalileoSVG from "images/b2b/bluePenguinGalileo.svg"
import bluePenguinKickerSVG from "images/b2b/bluePenguinKicker.svg"
import bluePenguinUpholdSVG from "images/b2b/bluePenguinUphold.svg"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import useMedia from "utils/useMedia"

interface Button {
  text?: string
  type?: "link" | "primary" | "secondary" | "round"
  rotate?: number
  arrow?: boolean
  download?: boolean
  lightMode?: boolean
  route?: string
  link?: string
  gradient?: {
    gradient: string
    fill: string
  }
}

export default function PartnerSpotlight({
  variant,
  localeImage = null,
}: {
  variant: "bluePenguin" | "shipt" | "galileo" | "kicker" | "uphold"
  localeImage?: IGatsbyImageData | null
}) {
  const { t, i18n } = useI18next()

  const images: Queries.B2BPartnerSpotlightQuery = useStaticQuery(graphql`
    query B2BPartnerSpotlight {
      kickerPerson: file(relativePath: { eq: "b2b/kickerPerson.webp" }) {
        ...LocalImageQuery
      }
      shiptPerson: file(relativePath: { eq: "b2b/shiptPerson.webp" }) {
        ...LocalImageQuery
      }
      upholdPerson: file(relativePath: { eq: "b2b/upholdPerson.webp" }) {
        ...LocalImageQuery
      }
      upholdPersonM: file(relativePath: { eq: "b2b/upholdPersonM.webp" }) {
        ...LocalImageQuery
      }
      galileoPerson: file(relativePath: { eq: "b2b/galileoPerson.webp" }) {
        ...LocalImageQuery
      }
      galileoPersonM: file(relativePath: { eq: "b2b/galileoPersonM.webp" }) {
        ...LocalImageQuery
      }
    }
  `)

  const kickerPerson = images.kickerPerson?.childImageSharp?.gatsbyImageData
  const shiptPerson = images.shiptPerson?.childImageSharp?.gatsbyImageData
  const upholdPerson = images.upholdPerson?.childImageSharp?.gatsbyImageData
  const upholdPersonM = images.upholdPersonM?.childImageSharp?.gatsbyImageData
  const galileoPerson = images.galileoPerson?.childImageSharp?.gatsbyImageData
  const galileoPersonM = images.galileoPersonM?.childImageSharp?.gatsbyImageData

  const innerWidth = useMedia("1440px", "86.747vw", "81.445vw", "89.333vw")
  const titleSize = useMedia(text.h3, text.h3, text.h3, text.h1Mobile)
  const textSize = useMedia(text.bodyR, text.bodyR, text.bodyR, text.bodyR)
  const title1Width = useMedia("583px", "35.12vw", "100%", "90vw")
  const text1Width = useMedia("644px", "38.795vw", "100%", "90vw")

  const shiptTitle = useMedia("483px", "29.096vw", "100%", "90vw")
  const shiptText = useMedia("566px", "34.096vw", "100%", "90vw")

  const kickerTitle = useMedia("483px", "29.096vw", "100%", "90vw")
  const kickerText = useMedia("566px", "34.096vw", "100%", "90vw")

  const upholdTitle = useMedia("516px", "31.084vw", "100%", "90vw")
  const upholdText = useMedia("516px", "31.084vw", "52vw", "89.333vw")
  const upHoldBody = useMedia(
    t(
      "Uphold was looking to expand their debit card program into the US to support their product proposition for the US market. They chose to partner with Galileo to bring their idea to life; a debit card that lets customers spend any asset in their wallet with a single tap."
    ),
    t(
      "Uphold was looking to expand their debit card program into the US to support their product proposition for the US market. They chose to partner with Galileo to bring their idea to life; a debit card that lets customers spend any asset in their wallet with a single tap."
    ),
    t(
      "Uphold was looking to expand their debit card program into the US to support their product proposition for the US market. They chose to partner with Galileo to bring their idea to life; a debit card that lets customers spend any asset in their wallet with a single tap."
    ),
    t(
      "The new Uphold card by Mastercard lets customers spend any asset in their wallet. They can change their source of funds in a single tap, giving them total control over how they use their investments to buy anything, any time—just like any other debit card."
    )
  )

  const galileoTitle = useMedia("644px", "38.795vw", "100%", "90vw")
  const galileoText = useMedia("566px", "34.096vw", "100%", "90vw")
  const galileoPersonImg = useMedia(
    galileoPerson,
    galileoPerson,
    galileoPerson,
    galileoPersonM
  )

  const upholdPersonImg = useMedia(
    upholdPerson,
    upholdPerson,
    upholdPerson,
    upholdPersonM
  )

  const borderRadius = useMedia("20px", "1.205vw", "1.953vw", "5.333vw")

  const defaultTitle = useMedia("483px", "29.096vw", "100%", "90vw")
  const defaultText = useMedia("566px", "34.096vw", "100%", "90vw")
  const [logoObject, setLogoObject] = useState({ img: "", alt: "" })
  const [title, setTitle] = useState("")
  const [body, setBody] = useState<React.ReactNode | string | null>("")
  const [bigPic, setBigPic] = useState<IGatsbyImageData | null | undefined>(
    null
  )
  const [reverse, setReverse] = useState(false)
  const [reverseColumn, setReverseColumn] = useState(false)
  const [buttons, setButtons] = useState<Button[] | undefined>(undefined)
  const [logoHeight, setLogoHeight] = useState({
    fullwidth: "40px",
    desktop: "2.41vw",
    tablet: "3.906vw",
    mobile: "10.667vw",
  })
  const [kicker, setKicker] = useState<React.ReactNode | null>(null)
  const [titleWidth, setTitleWidth] = useState("")
  const [textWidth, setTextWidth] = useState("")

  useMemo(() => {
    const trans = i18n.getFixedT("growth-and-activation")

    switch (variant) {
      case "bluePenguin":
        setTitleWidth(title1Width)
        setTextWidth(text1Width)
        setLogoObject({
          img: bluePenguinBlueSVG,
          alt: "Blue Penguin logo",
        })
        setTitle(
          t("Get to market fast and scale.") ?? "Get to market fast and scale."
        )
        setBody(
          t(
            "Galileo’s B2B payments technology is ideal for a sophisticated multi-network payments platform like the one created by B2B payments leader BluePenguin. They recognized Galileo as the API standard for card issuing and B2B banking and worked with us to bring their innovative B2B payments concepts to market—faster and at a greater scale than they imagined possible."
          )
        )

        setBigPic(localeImage)
        break
      case "shipt":
        setReverse(true)
        setTitleWidth(shiptTitle)
        setTextWidth(shiptText)
        setKicker(<Kicker kickerText="THE GIG ECONOMY" />)
        setTitle("THE GIG ECONOMY.")
        setBody(
          "The global gig economy is booming. And traditional payment methods aren’t keeping up. Streamline 1099 contractor payments and give valuable workers fast, secure access to their hard-earned money with real-time funding. Galileo can facilitate instant payment to physical or virtual cards, which also saves your company money on checks and ACH fees."
        )
        setBigPic(shiptPerson)
        break
      case "kicker":
        setKicker(
          <Kicker kickerText="Galileo gives you options" kickerIcon="discs2" />
        )
        setTitleWidth(kickerTitle)
        setTextWidth(kickerText)
        setLogoObject({
          img: bluePenguinKickerSVG,
          alt: "Galileo gives you options",
        })
        setTitle("Customers shouldn’t have to choose.")
        setBody(
          "What’s the point of investing if you can’t spend the money you earn? Yet, every day, customers are forced to choose between an investment account and a checking account. Now you have the power to change that—and build customer loyalty along the way."
        )
        setBigPic(kickerPerson)
        break
      case "uphold":
        setReverseColumn(true)
        setTitleWidth(upholdTitle)
        setTextWidth(upholdText)
        setLogoObject({ img: bluePenguinUpholdSVG, alt: "Uphold logo" })
        setTitle(
          t("Flexibility to spend any asset.") ??
            "Flexibility to spend any asset."
        )
        setBody(upHoldBody)
        setBigPic(upholdPersonImg)
        break
      case "galileo":
        setLogoHeight({
          fullwidth: "30px",
          desktop: "1.807vw",
          tablet: "2.93vw",
          mobile: "8vw",
        })
        setReverseColumn(true)
        setButtons([
          {
            type: "secondary",
            text: t("Learn More") ?? "Learn More",
            route:
              "https://docs.galileo-ft.com/pro/docs/maximizing-customer-activation-rates",
          },
        ])
        setTitleWidth(galileoTitle)
        setTextWidth(galileoText)
        setLogoObject({ img: bluePenguinGalileoSVG, alt: "Galileo logo" })
        setTitle(
          t("Improving onboarding and retention.") ??
            "Improving onboarding and retention."
        )

        setBody(
          <>
            <Trans>The biggest losses in customer engagement occur:</Trans>{" "}
            <br /> &bull; <Trans>Between account creation and activation</Trans>{" "}
            <br /> &bull;{" "}
            <Trans>
              Between activation and depositing funds into the account
            </Trans>
            <br />
            <br />
            <Trans>
              With these engagement points in mind, Galileo has developed
              products and features to guide your customers through the process
              so that they can activate, fund, and start spending from a new
              account as quickly as possible.
            </Trans>
          </>
        )
        setBigPic(galileoPersonImg)
        break

      default:
        setTitleWidth(defaultTitle)
        setTextWidth(defaultText)
        setLogoObject({ img: bluePenguinGalileoSVG, alt: "Galileo logo" })
        setTitle(
          trans("Improving onboarding and retention.") ??
            "Improving onboarding and retention."
        )
        setBody(
          trans(
            "Uphold was looking to expand their debit card program into the US to support their product proposition for the US market. They chose to partner with Galileo to bring their idea to life; a debit card that lets customers spend any asset in their wallet with a single tap."
          )
        )
        setBigPic(upholdPerson)
        break
    }
  }, [
    localeImage,
    defaultTitle,
    defaultText,
    variant,
    t,
    upHoldBody,
    galileoPersonImg,
    galileoText,
    galileoTitle,
    upholdPerson,
    upholdPersonImg,
    upholdText,
    shiptPerson,
    shiptText,
    shiptTitle,
    kickerPerson,
    kickerText,
    kickerTitle,
    title1Width,
    text1Width,
    upholdTitle,
    i18n,
  ])

  return (
    <Wrapper>
      <InnerWrapper setWidth={innerWidth}>
        <Content reverseColumn={reverseColumn} reverse={reverse}>
          <CalloutWrapper>
            {kicker ?? (
              <Logo
                src={logoObject.img}
                alt={logoObject.alt}
                logoHeight={logoHeight}
                loading="lazy"
              />
            )}
            <Callout
              variant="lightmode"
              title={{
                content: title,
                size: titleSize,
                width: titleWidth,
                color: colors.blue800,
              }}
              text={{
                width: textWidth,
                content: body,
                color: colors.blue200,
                size: textSize,
              }}
              buttons={buttons ?? undefined}
            />
          </CalloutWrapper>
          <ImageWrapper>
            {bigPic && (
              <ZoomImageGatsby
                borderRadius={borderRadius}
                src={bigPic}
                alt={t("Woman sitting at desk smiling")}
              />
            )}
          </ImageWrapper>
        </Content>
      </InnerWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 9.036vw 0;

  ${media.fullWidth} {
    padding: 150px 0;
  }
  ${media.tablet} {
    padding: 14.648vw 0;
  }
  ${media.mobile} {
    padding: 21.333vw 0;
  }
`

const Content = styled.div<{ reverse: boolean; reverseColumn: boolean }>`
  display: flex;
  align-items: center;
  gap: 7.892vw;
  ${props => (props.reverse ? "flex-direction: row-reverse;" : null)}
  ${media.fullWidth} {
    gap: 131px;
  }
  ${media.tablet} {
    ${props =>
      props.reverseColumn
        ? "flex-direction: column-reverse"
        : "flex-direction: column"};
    gap: 3.906vw;
  }
  ${media.mobile} {
    ${props =>
      props.reverseColumn
        ? "flex-direction: column-reverse"
        : "flex-direction: column"};
    gap: 10.667vw;
  }
`

const CalloutWrapper = styled.div`
  width: 38.795vw;
  ${media.fullWidth} {
    width: 644px;
  }
  ${media.tablet} {
    width: 100%;
  }
  ${media.mobile} {
    width: 100%;
  }
`

const Logo = styled.img<{ logoHeight: Record<string, string> }>`
  height: ${props => props.logoHeight.desktop};
  margin-bottom: 1.205vw;
  ${media.fullWidth} {
    height: ${props => props.logoHeight.fullwidth};
    margin-bottom: 20px;
  }
  ${media.tablet} {
    height: ${props => props.logoHeight.tablet};
    margin-bottom: 1.953vw;
  }
  ${media.mobile} {
    height: ${props => props.logoHeight.mobile};
    margin-bottom: 5.333vw;
  }
`

const ImageWrapper = styled.div`
  /* overflow: hidden; */
  ${media.fullWidth} {
    width: 646px;
    height: 550px;
  }
  ${media.desktop} {
    width: 38.916vw;
    height: 33.133vw;
  }

  ${media.tablet} {
    width: 81.445vw;
    height: auto;
  }

  ${media.mobile} {
    width: 89.333vw;
    height: 58.933vw;
  }
`
